import React, { useState } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Container,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Paper,
} from '@mui/material';
import { ArrowBack, CheckCircle, AddPhotoAlternate, Close } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSubmitInquiry } from '../../hooks/useInquiry';
import type { InquiryCategory } from '../../types/report';

const CATEGORIES: Array<{ value: InquiryCategory; label: string; sla: string }> = [
  { value: 'ACCOUNT', label: '계정 / 프로필', sla: '3영업일' },
  { value: 'PAYMENT', label: '결제 / 환불', sla: '3영업일' },
  { value: 'PRIVACY', label: '개인정보', sla: '10일 이내' },
  { value: 'TECHNICAL', label: '기술 문제', sla: '3영업일' },
  { value: 'REPORT', label: '신고하기', sla: '3영업일' },
  { value: 'OTHER', label: '기타', sla: '3영업일' },
];

const InquiryFormPage: React.FC = () => {
  const navigate = useNavigate();
  const [category, setCategory] = useState<InquiryCategory | ''>('');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [filePreviews, setFilePreviews] = useState<string[]>([]);
  const [submitted, setSubmitted] = useState(false);

  const { submit, loading, error } = useSubmitInquiry();

  const selectedCat = CATEGORIES.find((c) => c.value === category);

  const handleFileAdd = (e: React.ChangeEvent<HTMLInputElement>) => {
    const added = Array.from(e.target.files ?? []);
    const remaining = 5 - files.length;
    const toAdd = added.slice(0, remaining);
    setFiles((prev) => [...prev, ...toAdd]);
    toAdd.forEach((file) => {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (ev) => setFilePreviews((prev) => [...prev, ev.target?.result as string]);
        reader.readAsDataURL(file);
      } else {
        setFilePreviews((prev) => [...prev, '']);
      }
    });
    e.target.value = '';
  };

  const handleFileRemove = (idx: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
    setFilePreviews((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async () => {
    if (!category || !title.trim() || !content.trim()) return;
    const result = await submit({
      category,
      title: title.trim(),
      content: content.trim(),
      attachments: files.length > 0 ? files : undefined,
    });
    if (result) setSubmitted(true);
  };

  if (submitted) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
        <AppBar position="static" elevation={0}>
          <Toolbar>
            <IconButton edge="start" color="inherit" onClick={() => navigate(-1)} aria-label="뒤로가기">
              <ArrowBack />
            </IconButton>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>1:1 문의하기</Typography>
          </Toolbar>
        </AppBar>
        <Container maxWidth="sm" sx={{ py: 6, textAlign: 'center' }}>
          <CheckCircle sx={{ fontSize: 72, color: 'success.main', mb: 2 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>문의가 접수되었습니다</Typography>
          <Typography sx={{ color: 'text.secondary', mb: 4, fontSize: '1rem', lineHeight: 1.8 }}>
            {selectedCat?.sla ?? '3영업일'} 이내 답변드리겠습니다.
            <br />
            답변 완료 시 서비스 알림과 이메일로 안내드립니다.
          </Typography>
          <Button
            variant="contained"
            fullWidth
            size="large"
            sx={{ py: 1.5, fontSize: '1rem', fontWeight: 700, mb: 1.5 }}
            onClick={() => navigate('/support/inquiry/list')}
          >
            문의 내역 보기
          </Button>
          <Button
            variant="outlined"
            fullWidth
            size="large"
            sx={{ py: 1.5, fontSize: '1rem' }}
            onClick={() => navigate('/support')}
          >
            고객센터 홈
          </Button>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar position="static" elevation={0}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => navigate(-1)} aria-label="뒤로가기">
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>1:1 문의하기</Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="sm" sx={{ py: 3 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* 카테고리 */}
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel sx={{ fontSize: '1rem' }}>문의 유형 *</InputLabel>
          <Select
            value={category}
            label="문의 유형 *"
            onChange={(e) => setCategory(e.target.value as InquiryCategory)}
            sx={{ fontSize: '1rem' }}
          >
            {CATEGORIES.map((cat) => (
              <MenuItem key={cat.value} value={cat.value} sx={{ fontSize: '1rem' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                  <span>{cat.label}</span>
                  <Chip label={`답변 ${cat.sla}`} size="small" sx={{ fontSize: '0.75rem', ml: 1 }} />
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* 제목 */}
        <TextField
          fullWidth
          label="제목 *"
          placeholder="문의 제목을 입력해주세요"
          value={title}
          onChange={(e) => { if (e.target.value.length <= 100) setTitle(e.target.value); }}
          inputProps={{ style: { fontSize: '1rem' } }}
          helperText={`${title.length}/100`}
          FormHelperTextProps={{ sx: { textAlign: 'right' } }}
          sx={{ mb: 3 }}
        />

        {/* 내용 */}
        <TextField
          fullWidth
          multiline
          rows={6}
          label="내용 *"
          placeholder="문의 내용을 상세히 입력해주세요"
          value={content}
          onChange={(e) => { if (e.target.value.length <= 2000) setContent(e.target.value); }}
          inputProps={{ style: { fontSize: '1rem' } }}
          helperText={`${content.length}/2000`}
          FormHelperTextProps={{ sx: { textAlign: 'right' } }}
          sx={{ mb: 3 }}
        />

        {/* 파일 첨부 */}
        <Typography sx={{ fontWeight: 700, fontSize: '1rem', mb: 1 }}>
          파일 첨부{' '}
          <Typography component="span" sx={{ fontWeight: 400, color: 'text.secondary', fontSize: '0.9rem' }}>
            (선택, 이미지·PDF 최대 5장)
          </Typography>
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
          {files.map((file, idx) => (
            <Box key={idx} sx={{ position: 'relative', width: 80, height: 80 }}>
              {filePreviews[idx] ? (
                <img
                  src={filePreviews[idx]}
                  alt={file.name}
                  style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 8, border: '1px solid #ddd' }}
                />
              ) : (
                <Paper
                  elevation={0}
                  sx={{ width: 80, height: 80, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
                >
                  <Typography sx={{ fontSize: '0.65rem', textAlign: 'center', color: 'text.secondary', px: 0.5, wordBreak: 'break-all' }}>
                    {file.name}
                  </Typography>
                </Paper>
              )}
              <IconButton
                size="small"
                onClick={() => handleFileRemove(idx)}
                sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'background.paper', p: 0.25, border: '1px solid', borderColor: 'divider' }}
              >
                <Close fontSize="small" />
              </IconButton>
            </Box>
          ))}
          {files.length < 5 && (
            <label>
              <input type="file" accept="image/*,application/pdf" multiple hidden onChange={handleFileAdd} />
              <Box
                sx={{
                  width: 80, height: 80, border: '1px dashed', borderColor: 'divider',
                  borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', '&:hover': { borderColor: 'primary.main' },
                }}
              >
                <AddPhotoAlternate sx={{ color: 'text.secondary' }} />
              </Box>
            </label>
          )}
        </Box>

        <Button
          variant="contained"
          fullWidth
          size="large"
          disabled={!category || !title.trim() || !content.trim() || loading}
          onClick={handleSubmit}
          sx={{ py: 1.75, fontSize: '1.05rem', fontWeight: 700 }}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : '문의 접수하기'}
        </Button>
      </Container>
    </Box>
  );
};

export default InquiryFormPage;
